import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Treatment } from '@/lib/types';
import {
  Banner,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';
import { SearchInput, matches } from '@/components/SearchInput';
import { ntd } from '@/lib/format';

const CATEGORIES = ['雷射', '拉提', '注射', '保養', '諮詢'];

export function CatalogPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const blank = { code: '', name: '', short: '', category: '雷射', durationMin: 60, priceCents: 0, contra: '', regulated: false };
  const [form, setForm] = useState(blank);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const set = (k: keyof typeof form, v: string | number | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const treatments = useQuery({
    queryKey: ['treatments'],
    queryFn: () => api.get<Treatment[]>('/catalog/treatments'),
  });

  const openNew = () => {
    setEditId(null);
    setForm(blank);
    setOpen(true);
  };
  const openEdit = (t: Treatment) => {
    setEditId(t.id);
    setForm({
      code: t.code,
      name: t.name,
      short: t.short,
      category: t.category,
      durationMin: t.durationMin,
      priceCents: t.priceCents,
      contra: (t.contraTags ?? []).join('、'),
      regulated: (t as { regulated?: boolean }).regulated ?? false,
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        short: form.short || form.name,
        category: form.category,
        durationMin: Number(form.durationMin),
        priceCents: Number(form.priceCents) || 0,
        contraTags: form.contra
          .split(/[,，、]/)
          .map((s) => s.trim())
          .filter(Boolean),
        regulated: form.regulated,
      };
      return editId
        ? api.patch<Treatment>(`/catalog/treatments/${editId}`, body)
        : api.post<Treatment>('/catalog/treatments', { ...body, code: form.code });
    },
    onSuccess: () => {
      ok(editId ? '方案已更新' : '方案已上架 · 三店同步中');
      setOpen(false);
      setEditId(null);
      setForm(blank);
      void qc.invalidateQueries({ queryKey: ['treatments'] });
    },
    onError: fail,
  });

  const allList = treatments.data ?? [];
  const list = allList.filter(
    (t) =>
      (!q || matches(t.name, q) || matches(t.code, q) || matches(t.short, q)) &&
      (!cat || t.category === cat),
  );
  const ready = (editId || form.code) && form.name && form.durationMin > 0;

  const sortList = useSort(list, {
    code: (t) => t.code,
    name: (t) => t.name,
    category: (t) => t.category,
    duration: (t) => t.durationMin,
    price: (t) => t.priceCents,
  });

  return (
    <div>
      <PageHeader
        title="方案上架管理"
        subtitle="商品中台 · 一次上架，三店同步、LINE OA 自動推播"
        right={
          <button className="btn btn-primary" onClick={openNew}>
            <Icon d={I.plus} size={13} /> 新增方案
          </button>
        }
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="上架中療程" value={String(allList.length)} tone="primary" />
        <Stat label="分類數" value={String(new Set(allList.map((t) => t.category)).size)} />
        <Stat label="平均時長（分）" value={String(allList.length ? Math.round(allList.reduce((s, t) => s + t.durationMin, 0) / allList.length) : 0)} />
        <Stat label="平均定價" value={allList.length ? ntd(Math.round(allList.reduce((s, t) => s + t.priceCents, 0) / allList.length)) : 'NT$0'} tone="wallet" />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <SearchInput value={q} onChange={setQ} placeholder="搜尋療程名稱 / 代碼" width={260} />
        <div style={{ width: 140 }}>
          <Select value={cat} onChange={setCat} placeholder="全部分類" options={[{ value: '', label: '全部分類' }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{list.length} 筆</span>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="SKU" sortKey="code" sort={sortList} />
              <SortTh label="療程名稱" sortKey="name" sort={sortList} />
              <SortTh label="分類" sortKey="category" sort={sortList} />
              <SortTh label="時長" sortKey="duration" sort={sortList} align="right" />
              <SortTh label="定價" sortKey="price" sort={sortList} align="right" />
              <th style={thStyle}>狀態</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {sortList.sorted.map((t) => (
              <tr key={t.id}>
                <td style={cellStyle} className="num">
                  {t.code}
                </td>
                <td style={{ ...cellStyle, fontWeight: 600 }}>
                  {t.name}
                  {t.contraTags?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                      {t.contraTags.map((tag) => (
                        <Tag key={tag} tone="danger">
                          禁忌：{tag}
                        </Tag>
                      ))}
                    </div>
                  )}
                </td>
                <td style={cellStyle}>
                  <Tag tone="tag">{t.category}</Tag>
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  {t.durationMin} 分
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">
                  {ntd(t.priceCents)}
                </td>
                <td style={cellStyle}>
                  <Tag tone="success" dot>
                    上架中
                  </Tag>
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => openEdit(t)}>
                    <Icon d={I.edit} size={12} /> 編輯
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={7}>
                  尚無方案，點右上「新增方案」建立
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} width={520}>
        <ModalHeader title={editId ? '編輯療程方案' : '新增療程方案'} sub={editId ? 'SKU 代碼建立後不可更改' : '上架後三店同步可預約 / 開立堂票'} onClose={() => setOpen(false)} />
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
            <Field label="SKU 代碼 *">
              <input style={{ ...inputStyle, opacity: editId ? 0.6 : 1 }} className="num" value={form.code} disabled={!!editId} onChange={(e) => set('code', e.target.value)} placeholder="PICO-6" />
            </Field>
            <Field label="療程名稱 *">
              <input style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="皮秒雷射 6 堂尊榮包" />
            </Field>
          </div>
          <Field label="簡稱（顯示用）">
            <input style={inputStyle} value={form.short} onChange={(e) => set('short', e.target.value)} placeholder="皮秒" />
          </Field>
          <Field label="禁忌標籤（逗號分隔，開單時比對顧客禁忌症攔截）">
            <input style={inputStyle} value={form.contra} onChange={(e) => set('contra', e.target.value)} placeholder="抗凝血劑, 蟹足腫" />
          </Field>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '4px 0' }}>
            <input type="checkbox" checked={form.regulated} onChange={(e) => set('regulated', e.target.checked)} />
            特管辦法列管術式（需先完成術前審查才可預約執行）
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <Field label="分類">
              <Select
                value={form.category}
                onChange={(val) => set('category', val)}
                options={CATEGORIES.map((c) => ({ value: c, label: c }))}
              />
            </Field>
            <Field label="時長（分）*">
              <input type="number" style={inputStyle} className="num" value={form.durationMin} onChange={(e) => set('durationMin', Number(e.target.value))} />
            </Field>
            <Field label="定價（元）">
              <input type="number" style={inputStyle} className="num" value={form.priceCents / 100} onChange={(e) => set('priceCents', Number(e.target.value) * 100)} />
            </Field>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setOpen(false)}>
            取消
          </button>
          <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!ready || save.isPending} onClick={() => save.mutate()}>
            <Icon d={I.check} size={13} /> {editId ? '儲存變更' : '儲存並上架'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
